/**
 * WrappedSharePage  — /wrapped/s/:token
 *
 * Public (no auth) view of someone's Relationship Wrapped stats.
 * Identical layout to WrappedPage but read-only + signup CTA at the bottom.
 */

import { useEffect, useState } from 'react';
import { Link, useParams }     from 'react-router-dom';

interface WrappedStats {
  year:                      number;
  total_cards:               number;
  total_people:              number;
  total_countries:           number;
  most_carded_contact_name:  string | null;
  most_carded_contact_count: number;
  streak_years:              number;
}

const OCCASION_LABEL: Record<string, string> = {
  birthday:       'Birthdays',
  anniversary:    'Anniversaries',
  christmas:      'Christmas',
  valentines:     'Valentine\'s Day',
  mothers_day:    'Mother\'s Day',
  fathers_day:    'Father\'s Day',
  graduation:     'Graduation',
  thinking_of_you:'Thinking of You',
  custom:         'Special Moments',
};

// ── Simple stat row ───────────────────────────────────────────────────────────

function StatRow({ emoji, label, value }: { emoji: string; label: string; value: string | number }) {
  return (
    <div className="flex items-center gap-4 py-4 border-b border-white/10 last:border-0">
      <span className="text-3xl w-10 text-center flex-shrink-0">{emoji}</span>
      <div className="flex-1 min-w-0">
        <p className="font-body text-xs text-white/50 uppercase tracking-wider">{label}</p>
        <p className="font-heading text-3xl font-bold text-[#B59518] mt-0.5 leading-none">{value}</p>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function WrappedSharePage() {
  const { token }                        = useParams<{ token: string }>();
  const [stats,   setStats]              = useState<WrappedStats | null>(null);
  const [loading, setLoading]            = useState(true);
  const [notFound, setNotFound]          = useState(false);

  useEffect(() => {
    if (!token) return;
    fetch(`/api/wrapped/share/${token}`)
      .then(async (res) => {
        if (res.status === 404) { setNotFound(true); return; }
        if (!res.ok) throw new Error();
        setStats(await res.json());
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [token]);

  // ── Not found ───────────────────────────────────────────────────────────────
  if (!loading && notFound) {
    return (
      <div className="min-h-screen bg-cream flex flex-col items-center justify-center p-6 text-center">
        <div className="text-5xl mb-5">🤷</div>
        <h1 className="font-heading text-2xl text-navy mb-3">Wrapped not found</h1>
        <p className="font-body text-sm text-gray-500 mb-6">
          This link may have expired or the user hasn't generated their Wrapped yet.
        </p>
        <Link to="/" className="font-body text-sm font-medium text-sage hover:underline">
          Discover MomentSent →
        </Link>
      </div>
    );
  }

  // ── Loading ─────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-[#1A1410] flex items-center justify-center">
        <div className="w-10 h-10 rounded-full border-2 border-[#B59518] border-t-transparent animate-spin" />
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div className="min-h-screen bg-[#1A1410]">

      {/* Logo */}
      <div className="text-center pt-8 pb-2">
        <Link to="/" className="font-heading text-2xl font-bold text-white hover:opacity-80 transition-opacity">
          Moment<span className="text-[#B59518]">Sent</span>
        </Link>
      </div>

      {/* Hero */}
      <div className="py-10 px-4 text-center">
        <p className="font-body text-[#B59518] text-xs font-semibold uppercase tracking-widest mb-3">
          Relationship Wrapped {stats.year}
        </p>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white">
          {stats.year} in Cards
        </h1>
        <p className="font-body text-white/50 text-sm mt-3">
          Someone showed up for the people they love, {stats.total_cards} times.
        </p>
      </div>

      {/* Stats card */}
      <div className="max-w-md mx-auto px-4 pb-6">
        <div className="bg-white/5 border border-white/10 rounded-[24px] overflow-hidden">
          <div className="px-6 py-2">
            <StatRow emoji="📬" label="Cards sent"           value={stats.total_cards.toLocaleString()} />
            <StatRow emoji="❤️"  label="People shown love"  value={stats.total_people.toLocaleString()} />
            {stats.total_countries > 1 && (
              <StatRow emoji="🌍" label="Countries reached" value={stats.total_countries} />
            )}
            {stats.most_carded_contact_name && stats.most_carded_contact_count > 1 && (
              <StatRow
                emoji="🏆"
                label="Most-loved person"
                value={stats.most_carded_contact_name}
              />
            )}
            {stats.streak_years > 1 && (
              <StatRow emoji="🔥" label="Year streak"       value={`${stats.streak_years} years`} />
            )}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-8 text-center">
          <p className="font-body text-white/70 text-sm mb-5">
            Want your own Relationship Wrapped? MomentSent automatically sends physical cards to the people you love.
          </p>
          <Link
            to="/signup"
            className="inline-block px-8 py-3 rounded-full bg-[#B59518] text-[#1A1410]
                       font-body font-semibold text-sm hover:bg-[#B59518]/90 transition-colors shadow-lg"
          >
            Start free, no credit card required →
          </Link>
          <p className="font-body text-xs text-white/30 mt-4">14-day free trial</p>
        </div>

        {/* Footer */}
        <p className="text-center font-body text-xs text-white/20 mt-10 pb-8">
          © {new Date().getFullYear()} MomentSent LLC, Hubert, NC
        </p>
      </div>
    </div>
  );
}
